import { useCallback, useEffect, useMemo, useState } from "react";
import { ObjectListItem } from "../../../coobjects/object-list-panel/ObjectList";
import { CoObjectItem, CoObjectSummary } from "../../../interfaces/CoObject";
import { Tag } from "../../../interfaces/Tag";
import { getAllCoObjects } from "../../../../utils/helpers/ContentControlHelper";
import NotificationManager from "../../../../common/notification/NotificationManager";
import { VALIDATION_NOTIFICATION } from "../../../../utils/constants/notification";
import { formatMessage } from "../../../../utils/helpers/LanguageHelper";
import { addTagToCustomXml, editTagInCustomXml } from "../../../../utils/helpers/CustomXmlCoTagHelper";
import { remapAndPersistRules } from "../../../../utils/helpers/TagMergeHelper";
import { getRulesFromCustomXml } from "../../../../utils/helpers/CustomXmlCoRuleHelper";
import { getTags } from "../../../../utils/helpers/TagsHelper";
import { generatePrefixedId } from "../../../../utils/helpers/IdHelper";
import { CoObjectTypeEnum } from "../../../../api/models";
import { Rule } from "../../../interfaces/Rule";
import { useAppSelector } from "../../../../redux/hooks/hooks";

export interface AddTagProps {
  coObjects: CoObjectItem[];
  tags: Tag[];
  updateTags?: (tags: Tag[]) => void;
  updateRules?: (rules: Rule[]) => void;
  isShown: boolean;
  onHide: () => void;
  selectedTag?: Tag;
}

export interface AddTagResult {
  listObjects: ObjectListItem[];
  setSelectedObjects: (objects: CoObjectItem[]) => void;
  selectedCellItems: CoObjectItem[];
  autocompletes: string[];
  tagLabel: string | undefined;
  setTagLabel: (v: string) => void;
  getSelectionAsTagObject: () => CoObjectSummary[];
  handleBack: () => void;
  handleSave: () => void;
  resetModalState: () => void;
  preselectedCellsMap?: Map<string, CoObjectSummary[]>;
  selectedTag: Tag;
  hasPendingAgentEdit: boolean;
}

const mapObjectsForObjectList = (coObjects: CoObjectItem[], selectedIds?: Set<string>): ObjectListItem[] => {
  return coObjects.map((obj) => ({
    object: obj,
    selected: selectedIds != null && obj.id != null ? selectedIds.has(obj.id) : false,
    disableSelection: false,
    highlighted: false,
    buttons: [],
  }));
};

const getUpdatedSelection = (list: ObjectListItem[], selectedIds: Set<string>) =>
  list.map((obj) => {
    const isSelected = selectedIds.has(obj.object.id);
    return obj.selected === isSelected ? obj : { ...obj, selected: isSelected };
  });

export function useAddTagModal(props: AddTagProps): AddTagResult {
  const { coObjects, tags, updateTags, updateRules, onHide, selectedTag, isShown } = props;

  const stagedTagEdits = useAppSelector((state) => state.askAiChat.stagedTagEdits);
  const hasPendingAgentEdit = useMemo(
    () => !!selectedTag && stagedTagEdits.some((e) => e.targetId === selectedTag.id),
    [selectedTag, stagedTagEdits]
  );

  const selectedTagIds = useMemo(() => new Set((selectedTag?.objects ?? []).map((o) => o.id)), [selectedTag]);

  const [listObjects, setListObjects] = useState<ObjectListItem[]>(mapObjectsForObjectList(coObjects));
  const [tagLabel, setTagLabel] = useState<string>(selectedTag?.label);
  const [selectedCellItems, setSelectedCellItems] = useState<CoObjectItem[]>([]);
  // Captured by the refresh-on-open effect so handleSave can join the
  // persisted tag with the same fresh object list shown in the modal,
  // instead of the stale boot-time Redux coObjects (IDS-6546 follow-up).
  const [freshCoObjects, setFreshCoObjects] = useState<CoObjectItem[] | null>(null);

  const autocompletes = useMemo(
    () =>
      Array.from(
        new Set(
          tags
            .filter((tag) => !tag.isDeleted)
            .map((tag) => tag.label)
            .filter(Boolean)
        )
      ),
    [tags]
  );

  useEffect(() => {
    setListObjects(mapObjectsForObjectList(coObjects, selectedTagIds));
  }, [coObjects, selectedTagIds]);

  // Re-fetch document objects every time the modal is opened (isShown rises to
  // true).  props.coObjects may be stale: it is loaded once at app-boot into
  // Redux and never automatically refreshed.  Without this re-fetch, tables or
  // amounts added or deleted after the initial load would not appear in (or
  // disappear from) the taggable-objects list shown to the user inside the modal.
  useEffect(() => {
    if (!isShown) return;
    getAllCoObjects(["TABLE", "AMOUNT"])
      .then((all) => {
        const fresh = all.filter((o) => o.source?.elabResultType !== "IMAGE_STRINGBASE64");
        setFreshCoObjects(fresh);
        setListObjects(mapObjectsForObjectList(fresh, selectedTagIds));
      })
      .catch((e) => console.error("[AddTagModalHook] Failed to refresh objects on open:", e));
    // selectedTagIds is captured from closure at the moment the modal opens and
    // remains stable for the lifetime of a single modal session (selectedTag is
    // always set before isShown becomes true).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown]);

  const setSelectedObjects = useCallback((objects: CoObjectItem[]) => {
    const cellItems = objects.filter((o) => o.info.objectType === CoObjectTypeEnum.TABLE_CELL);
    const regularItems = objects.filter((o) => o.info.objectType !== CoObjectTypeEnum.TABLE_CELL);
    const selectedIds = new Set(regularItems.map((o) => o.id));
    setListObjects((prevList) => getUpdatedSelection(prevList, selectedIds));
    setSelectedCellItems(cellItems);
  }, []);

  const getSelectionAsTagObject = useCallback(() => {
    const fromList = listObjects
      .filter((o) => o.selected)
      .map<CoObjectSummary>((o) => ({
        ...o.object,
        id: o.object.id,
        objectType: o.object.info.objectType,
      }));
    const fromCells = selectedCellItems.map<CoObjectSummary>((cell) => ({
      ...cell,
      id: cell.id,
      objectType: cell.info.objectType,
      row: 1,
      column: 1,
    }));
    return [...fromList, ...fromCells];
  }, [listObjects, selectedCellItems]);

  const resetModalState = useCallback(() => {
    setTagLabel("");
    setSelectedObjects([]);
  }, [setSelectedObjects]);

  const handleBack = useCallback(() => {
    onHide();
    resetModalState();
  }, [resetModalState]);

  const handleSave = useCallback(() => {
    const onSave = async () => {
      try {
        let newTag: Tag;
        if (selectedTag) {
          newTag = {
            ...selectedTag,
            label: tagLabel,
            objects: getSelectionAsTagObject(),
          };
          const editResult = await editTagInCustomXml(newTag);
          if (editResult.merged && editResult.sourceTagId && editResult.destinationTagId) {
            await remapAndPersistRules(editResult.sourceTagId, editResult.destinationTagId);
            const updatedRules = await getRulesFromCustomXml();
            updateRules?.(updatedRules);
          }
        } else {
          newTag = {
            id: generatePrefixedId("TAG"),
            label: tagLabel,
            objects: getSelectionAsTagObject(),
          };

          await addTagToCustomXml(newTag);
        }

        const enrichedTags = await getTags(freshCoObjects ?? coObjects);

        updateTags?.(enrichedTags);
        NotificationManager.addNotification({
          key: VALIDATION_NOTIFICATION,
          message: tagLabel,
          title: formatMessage(selectedTag ? "cof.validation.title.tagupdated" : "cof.validation.title.tagcreated"),
          type: "success",
          closable: true,
          removeOnClose: true,
          isTemporary: true,
        });
        resetModalState();
        onHide();
      } catch (e) {
        console.error("[AddTagModalHook] Failed to save tag:", e);
        NotificationManager.addNotification({
          key: VALIDATION_NOTIFICATION,
          message: formatMessage("cof.validation.title.createtag.error"),
          title: formatMessage("cof.base.error"),
          type: "error",
          closable: true,
          removeOnClose: true,
          isTemporary: true,
        });
      }
    };
    void onSave();
  }, [
    tagLabel,
    getSelectionAsTagObject,
    freshCoObjects,
    coObjects,
    updateTags,
    updateRules,
    resetModalState,
    selectedTag,
  ]);

  const preselectedCellsMap = useMemo(() => {
    const map = new Map<string, CoObjectSummary[]>();
    selectedTag?.objects?.forEach((o) => {
      if (o.objectType !== CoObjectTypeEnum.TABLE_CELL) return;
      const id = String(o.id);
      const idx = id.indexOf("_");
      if (idx === -1) return;
      const tableId = id.slice(0, idx);
      map.set(tableId, [...(map.get(tableId) ?? []), o]);
    });
    return map;
  }, [selectedTag]);

  return {
    listObjects,
    setSelectedObjects,
    selectedCellItems,
    autocompletes,
    tagLabel,
    setTagLabel,
    getSelectionAsTagObject,
    handleBack,
    handleSave,
    resetModalState,
    preselectedCellsMap,
    selectedTag,
    hasPendingAgentEdit,
  };
}
